import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { store } from '@app/store';
import {
  selectBankAccounts,
  selectActiveCompany,
  selectIsCompanyDataLoading,
} from '@features/workspace/store/selectors';
import {
  createBankAccount,
  createTraderootMember,
  fetchBankAccounts,
  fetchCompany,
} from '@features/workspace/store/actions';
import { setNotification } from '@store/features/notification';

export enum JobIdEnum {
  CreateTraderootMember = 'CreateTraderootMember',
  CreateBankAccount = 'CreateBankAccount',
}

const JobsList: JobIdEnum[] = [
  JobIdEnum.CreateTraderootMember,
  JobIdEnum.CreateBankAccount,
];

export enum JobStatusEnum {
  Upcoming = 'Upcoming',
  Pending = 'Pending',
  Passed = 'Passed',
  Failed = 'Failed',
}

export interface IJob {
  title: string;
  exec: () => Promise<void>;
  checkIsPassed: () => boolean;
}

export interface IJobInfo {
  id: JobIdEnum;
  status: JobStatusEnum;
  title: string;
}

const useApprove = () => {
  const dispatch = useAppDispatch();
  const isCompanyDataLoading = useAppSelector(selectIsCompanyDataLoading);

  const [isApproveInProgress, setIsApprovePending] = useState(false);
  const hasError = useRef(false);

  const jobs: Record<JobIdEnum, IJob> = {
    [JobIdEnum.CreateTraderootMember]: {
      title: 'Traderoot member',
      exec: async () => {
        const company = selectActiveCompany(store.getState());

        await dispatch(createTraderootMember(company?.id ?? '')).unwrap();
        await dispatch(fetchCompany(null));
        await dispatch(fetchBankAccounts(null));
      },
      checkIsPassed: () => {
        const company = selectActiveCompany(store.getState());

        return !!company?.traderootMemberReference;
      },
    },
    [JobIdEnum.CreateBankAccount]: {
      title: 'Traderoot bank account',
      exec: async () => {
        const company = selectActiveCompany(store.getState());

        await dispatch(
          createBankAccount({
            companyId: company?.id ?? '',
            accountAlias: company?.name ?? '',
            accountProductType: 0,
          })
        ).unwrap();
        await dispatch(fetchBankAccounts(null)).unwrap();
      },
      checkIsPassed: () => {
        const bankAccounts = selectBankAccounts(store.getState());

        return !!bankAccounts?.length;
      },
    },
  };

  const resolveJobStatus = (jobId: JobIdEnum) => {
    return jobs[jobId].checkIsPassed()
      ? JobStatusEnum.Passed
      : JobStatusEnum.Upcoming;
  };

  const resolveJobsStatus = () => ({
    [JobIdEnum.CreateTraderootMember]: resolveJobStatus(
      JobIdEnum.CreateTraderootMember
    ),
    [JobIdEnum.CreateBankAccount]: resolveJobStatus(
      JobIdEnum.CreateBankAccount
    ),
  });

  useEffect(() => {
    if (!isCompanyDataLoading) {
      setJobsStatus(resolveJobsStatus());
    }
  }, [isCompanyDataLoading]);

  const [jobsStatus, setJobsStatus] = useState<
    Record<JobIdEnum, JobStatusEnum>
  >(resolveJobsStatus());

  const setJobStatus = (jobId: JobIdEnum, status: JobStatusEnum) => {
    hasError.current = true;
    setJobsStatus((prev) => ({
      ...prev,
      [jobId]: status,
    }));
  };

  const execJob = async (jobId: JobIdEnum) => {
    try {
      setJobStatus(jobId, JobStatusEnum.Pending);
      await jobs[jobId].exec();
      setJobStatus(jobId, JobStatusEnum.Passed);
    } catch (err) {
      setJobStatus(jobId, JobStatusEnum.Failed);
    }
  };

  const isApproved = useMemo(
    () =>
      JobsList.reduce(
        (acc, jobId) => acc && jobsStatus[jobId] === JobStatusEnum.Passed,
        true
      ),
    [JobsList, jobsStatus]
  );

  const approve = async () => {
    setIsApprovePending(true);
    // Create traderoot memeber if not created yet
    if (!jobs[JobIdEnum.CreateTraderootMember].checkIsPassed()) {
      await execJob(JobIdEnum.CreateTraderootMember);
    }
    // Create traderoot account if not created yet and traderoot member exists
    if (
      !jobs[JobIdEnum.CreateBankAccount].checkIsPassed() &&
      jobs[JobIdEnum.CreateTraderootMember].checkIsPassed()
    ) {
      await execJob(JobIdEnum.CreateBankAccount);
    }
    setIsApprovePending(false);
    if (!hasError.current) {
      dispatch(
        setNotification({
          message: 'Bank account successfully created',
          variant: 'success',
        })
      );
    }
  };

  const jobsInfo = useMemo<Record<JobIdEnum, IJobInfo>>(
    () => ({
      [JobIdEnum.CreateTraderootMember]: {
        id: JobIdEnum.CreateTraderootMember,
        status: jobsStatus[JobIdEnum.CreateTraderootMember],
        title: jobs[JobIdEnum.CreateTraderootMember].title,
      },
      [JobIdEnum.CreateBankAccount]: {
        id: JobIdEnum.CreateBankAccount,
        status: jobsStatus[JobIdEnum.CreateBankAccount],
        title: jobs[JobIdEnum.CreateBankAccount].title,
      },
    }),
    [jobsStatus]
  );

  return {
    approve,
    isApproved,
    isApproveInProgress,
    jobsInfo,
    execJob,
  };
};

export default useApprove;
